import { TabPanel, TabView } from "primereact/tabview";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CustomLoader } from "../custom-loader/custom-loader";
import { SERVICES } from "../../../core/services/services";
import { Icon } from "../../icons/icon";
import { SpecialLabelCases } from "../../models/utilities";
import { HELPER } from "../../helper/helper";
import { Toast } from "primereact/toast";
import { Optional } from "../optional/optional";
import { FRONTOFFICE_API } from "../../../core/apis/frontoffice-apis";
import { BACKOFFICE_API } from "../../../core/apis/backoffice-apis";
import { UserTypes } from "../../constants";
import { CustomModal } from "../custom-modal/custom-modal";
import { NewDispute } from "../../../components/back-office/disputes/new-dispute";
import { TokenDetailsByReference } from "./token-details";
import { MandateDetails } from "../mandates/mandate-details";
import { DisputeNotification } from "../dispute-notification/dispute-notification";
import { DisputeDetails } from "../../../components/back-office/disputes/dispute-details";
import { useHistory, useParams } from "react-router";
import { CustomBreadcrumb } from "../custom-breadcrumb/custom-breadcrumb";

export function TransactionDetailsPage(props) {
  const toast = useRef(null);
  const history = useHistory();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState(null);
  const [transactionDetails, setTransactionDetails] = useState([]);
  const [gettingTransactionDetails, setGettingTransactionDetails] =
    useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentModalIndex, setCurrentModalIndex] = useState(0);
  const [userType] = useState(props.userType);
  const [tokenReference, setTokenReference] = useState(null);
  const [mandateReference, setMandateReference] = useState(null);
  const [disputeableTransactionStatuses, setDisputeableTransactionStatuses] =
    useState([]);
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [transactionDisputeData, setTransactionDisputeData] = useState(null);
  const { transactionReference } = useParams();

  const getDisputeableTransactionStatus = useCallback(() => {
    const url = `/${
      props.userType === UserTypes.Frontoffice ? "frontoffice" : "backoffice"
    }/dispute/disputable-transaction-status`;
    SERVICES.MAKE_GET_REQUEST(url)
      .then((data) => {
        setDisputeableTransactionStatuses(data?.result);
      })
      .catch((error) => {
        toast.current.show({
          severity: "error",
          summary: "Error getting disputeable transaction statuses!",
          detail: HELPER.PROCESS_ERROR(error, "TOAST"),
          life: 10000,
        });
      });
  }, [props.userType]);

  const getDisputeByTransactionReference = useCallback(() => {
    const url = `/${
      props.userType === UserTypes.Frontoffice ? "frontoffice" : "backoffice"
    }/dispute/transaction/${transactionReference}`;
    SERVICES.MAKE_GET_REQUEST(url)
      .then((data) => {
        const result = data?.result;
        //  const hasDisputeLogged = result !== null;
        if (result) {
          setTransactionDisputeData(result);
        } else {
          getDisputeableTransactionStatus();
        }
      })
      .catch((error) => {
        getDisputeableTransactionStatus();
      });
  }, [getDisputeableTransactionStatus, props.userType, transactionReference]);

  useEffect(() => {
    getDisputeByTransactionReference();
  }, [getDisputeByTransactionReference]);

  const getApiUrl = useCallback(() => {
    if (props.userType === UserTypes.Frontoffice) {
      return FRONTOFFICE_API.GET_SINGLE_DIRECT_DEBIT_TRANSACTION;
    } else {
      return BACKOFFICE_API.GET_SINGLE_DIRECT_DEBIT_TRANSACTION;
    }
  }, [props.userType]);

  const handleShowMandateDetails = useCallback(
    (mandateReference) => {
      setMandateReference(mandateReference);
      setCurrentIndex(2);
      props.onShowMandateDetails();
    },
    [props]
  );

  const handleShowTokenDetails = useCallback((data) => {
    setTokenReference(data.tokenReference);
    openModal(0);
  }, []);

  const handleSuccess = useCallback(
    (data) => {
      const result = data.result;
      let arr = [];
      setTransactionStatus(result?.status);
      setTransactionDetails([]);
      for (const property in result) {
        arr.push({
          label: HELPER.CAMEL_TO_TITLE_CASE(property),
          value: result[property],
          itemCase: property,
          ...(property === "mandateReference" &&
            (HELPER.HAS_AUTHORITY("bo_view_debit_mandates") ||
              HELPER.HAS_AUTHORITY("fo_view_debit_mandates")) && {
              onclick: () => {
                const mandateReference = result[property];
                handleShowMandateDetails(mandateReference);
              },
            }),
          ...(property === "tokenReference" &&
            (HELPER.HAS_AUTHORITY("bo_view_debit_mandates") ||
              HELPER.HAS_AUTHORITY("fo_view_debit_mandates")) && {
              onclick: () => {
                // const tokenReference = result[property];
                handleShowTokenDetails(result);
              },
            }),
        });
      }
      setTransactionDetails(arr);
      setCurrentIndex(0);
      setGettingTransactionDetails(false);
    },
    [handleShowMandateDetails, handleShowTokenDetails]
  );

  const getTransactionDetails = useCallback(() => {
    setGettingTransactionDetails(true);
    const url = getApiUrl();
    SERVICES.MAKE_GET_REQUEST(`${url}/${transactionReference}`)
      .then((data) => {
        handleSuccess(data);
      })
      .catch((error) => {
        setError(HELPER.PROCESS_ERROR(error));
        setCurrentIndex(1);
        setGettingTransactionDetails(false);
      });
  }, [getApiUrl, handleSuccess, transactionReference]);

  useEffect(() => {
    getTransactionDetails();
  }, [getTransactionDetails]);

  const transformView = (itemCase, value) => {
    let result = value;
    if (itemCase) {
      SpecialLabelCases.forEach((e) => {
        if (e.case === itemCase) {
          result = e.action(value);
        }
      });
    }
    return result;
  };

  function openModal(index) {
    setCurrentModalIndex(index);
    setIsModalVisible(true);
  }

  function closeModal(reload) {
    setIsModalVisible(false);
    if (reload === true) {
      // setCurrentIndex(0);
      getTransactionDetails();
      getDisputeByTransactionReference();
    }
  }

  function handleShowDisputeDetails() {
    props.onShowDisputeDetails();
    setCurrentIndex(3);
  }

  const detailsView = () => {
    return transactionDetails.map((details, index) => (
      <div key={index.toString()} className="p-grid dcir-row">
        <div className="p-col-6">
          <p className="details-label">{details.label}:</p>
        </div>
        <div className="p-col-6 dd-trxn-deets">
          <Optional show={details.onclick}>
            <button
              // className="details-value bare-button"
              className="p-button-success view-details"
              onClick={() => {
                details.onclick();
              }}
            >
              {details.value}
            </button>
          </Optional>
          <Optional show={!details.onclick}>
            <p className="details-value">
              {transformView(details.itemCase, details.value)}
            </p>
          </Optional>
        </div>
      </div>
    ));
  };

  const errorView = () => {
    return (
      <div>
        <div className="empty-container">
          <Icon icon="error-message-icon" />
          <div>
            <p className="empty-text">{error}</p>
          </div>
          <div className="p-mt-3 p-text-center">
            <div style={{ maxWidth: "200px" }}>
              <button className="primary-button">Reload</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const transactionBreakdownTabView = () => {
    return (
      <div
        style={{ backgroundColor: "#ffffff", paddingTop: "1em" }}
        className="p-shadow-1 p-pb-5"
      >
        <div className="p-text-left p-ml-3 p-mt-2 p-pb-3">
          <span onClick={goBack} className="add-cursor">
            <span>
              <Icon icon="go-back-icon" />
            </span>
          </span>
        </div>
        <TabView>
          <TabPanel header="Transaction Summary">
            <div className="p-text-left">
              <div className="p-grid p-mt-2">
                <div className="p-col-8" />
                <Optional
                  show={
                    (HELPER.HAS_AUTHORITY("bo_dd_create_dispute") ||
                      HELPER.HAS_AUTHORITY("fo_dd_create_dispute")) &&
                    disputeableTransactionStatuses.includes(
                      transactionStatus
                    ) &&
                    !transactionDisputeData
                  }
                >
                  <div className="p-col-4">
                    <div
                      style={{
                        width: "135px",
                        float: "right",
                      }}
                    >
                      {/* {renderLogDisputeButton()} */}
                      <button
                        onClick={() => openModal(1)}
                        className="primary-button"
                      >
                        Log Dispute
                      </button>
                    </div>
                  </div>
                </Optional>
              </div>
              <Optional show={gettingTransactionDetails}>
                <div
                  className="p-mt-5 p-text-center"
                  style={{ padding: "10rem 0" }}
                >
                  <CustomLoader loadingText="Loading transaction details ..." />
                </div>
              </Optional>
              <Optional show={!gettingTransactionDetails}>
                <div className="p-grid p-mt-3">
                  <Optional show={transactionDisputeData !== null}>
                    <DisputeNotification
                      resolutionStatus={
                        transactionDisputeData?.resolutionStatus
                      }
                      logCode={transactionDisputeData?.logCode}
                      onShowDisputeDetails={handleShowDisputeDetails}
                    />
                  </Optional>
                  <div className="p-col-12">
                    <div>
                      <div className="p-text-left">{detailsView()}</div>
                    </div>
                  </div>
                </div>
              </Optional>
            </div>
          </TabPanel>
        </TabView>
      </div>
    );
  };

  const modalContent = () => {
    // eslint-disable-next-line default-case
    switch (currentModalIndex) {
      case 0:
        return (
          <TokenDetailsByReference
            userType={props.userType}
            tokenReference={tokenReference}
            closeModal={closeModal}
            // details={transactionDetails}
          />
        );
      case 1:
        return (
          <NewDispute
            transactionReference={transactionReference}
            closeDisputeModal={closeModal}
            userType={userType}
          />
        );
    }
  };

  function goBack() {
    if (currentIndex === 0) {
      history.goBack();
    } else {
      setCurrentIndex(0);
    }
  }

  const transactionBreakdownView = () => {
    // eslint-disable-next-line default-case
    switch (currentIndex) {
      case 0:
        return transactionBreakdownTabView();
      case 1:
        return errorView();
      case 2:
        return (
          <MandateDetails
            userType={props.userType}
            mandateReference={mandateReference}
            goBack={() => {
              setCurrentIndex(0);
              props.onHideMandateDetails();
            }}
          />
        );
      case 3:
        return (
          <DisputeDetails
            logCode={transactionDisputeData?.logCode}
            goBack={() => {
              props.onHideDisputeDetails();
              goBack();
            }}
            userType={props.userType}
          />
        );
    }
  };

  return (
    <div>
      <div>
        <Toast ref={toast} />
      </div>
      <div>
        <CustomModal
          onHide={closeModal}
          visible={isModalVisible}
          modalContent={modalContent}
          closeModal={closeModal}
        />
      </div>
      <div className="page-title p-text-left">Transactions</div>
      <div className="p-mt-2 p-mb-5">
        <CustomBreadcrumb page="Manage Transactions" />
      </div>
      {transactionBreakdownView()}
    </div>
  );
}
